/* eslint-disable jsx-a11y/label-has-associated-control */
import { ArrowRight } from '@mui/icons-material';
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormHelperText,
  IconButton,
  Stack,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import classNames from 'classnames';
import { Form, FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import WebSocket from '../../../api/web-socket/WebSocket';
import DateRangeModal, {
  EDateRangeModalVariants,
} from '../../../common-components/date-range-modal/date-range-modal';
import ErrorPanel from '../../../common-components/error-components/error-panel/error-panel';
import MuiSelectFieldControlled from '../../../common-components/form-fields/mui-select-field-controlled';
import LoadingPanel from '../../../common-components/loading-panel/loading-panel';
import { useAppDispatch, useAppSelector, useMobile } from '../../../hooks';
import { HistoryFormValues } from '../history-form/history-form-validators';
import '../history-form/history-form.scss';
import HistoryPanelSampleRate from '../history-panel-sample-rate';
import { setHistoryFormValues } from '../history-panel.slice';
import useHistoryAisApi from './use-history-ais-api';

export const ERROR_FETCH =
  "Sorry, we couldn't retrieve the Vessel History at the moment. Please try again later.";
export const ERROR_NO_RESULTS =
  'No Vessel History found for the provided identifiers. Please double-check your input and try again.';

const DUPLICATE_IDENTIFIERS_ERROR = 'Duplicate identifiers found.';
const INVALID_IDENTIFIERS_FORMAT_ERROR =
  'Invalid identifiers format. Please enter 7 or 9 digit numbers separated by spaces.';
const MAX_IDENTIFIERS_ERROR = 'Maximum of 50 identifiers allowed.';
const LARGE_REQUEST_WARNING =
  'This search may take some time due to the size of the request.';
export const MAX_IDENTIFIERS = 50;
export const WARN_IDENTIFIER_COUNT = 10;
export const ONE_YEAR_IN_MS = 365 * 24 * 60 * 60 * 1000;

const splitIdentifiers = (value?: string) =>
  (value ?? '').split(/\s+/).filter((identifier) => identifier.length > 0);

const isDateRangeOverOneYear = (fromDate: string, toDate: string) => {
  const fromDateValue = new Date(fromDate);
  const toDateValue = new Date(toDate);

  if (
    Number.isNaN(fromDateValue.getTime()) ||
    Number.isNaN(toDateValue.getTime())
  ) {
    return false;
  }

  return toDateValue.getTime() - fromDateValue.getTime() > ONE_YEAR_IN_MS;
};

const identifierSchema = Yup.string()
  .required('')
  .test('duplicate-identifiers', DUPLICATE_IDENTIFIERS_ERROR, (value = '') => {
    const identifiers = splitIdentifiers(value);
    return new Set(identifiers).size === identifiers.length;
  })
  .test(
    'identifier-limit',
    MAX_IDENTIFIERS_ERROR,
    (value = '') => splitIdentifiers(value).length <= MAX_IDENTIFIERS
  )
  .test(
    'is-valid-identifiers',
    INVALID_IDENTIFIERS_FORMAT_ERROR,
    (value = '') =>
      splitIdentifiers(value).every((identifier) =>
        identifier.match(/^\d{7}$|^\d{9}$/)
      )
  );

const historyValidationSchema = Yup.object().shape({
  fromDate: Yup.date().required('Required'),
  toDate: Yup.date().required('Required'),
  sampleRate: Yup.string().required('Required'),
  identifiers: identifierSchema,
});

function HistoryFormAisApi() {
  const dispatch = useAppDispatch();
  const { loading, error, formValues } = useAppSelector(
    (state) => state.historyPanel
  );

  const {
    isActive,
    isLoadingSlowly,
    noResults,
    onSubmit,
    onCancel,
    websocketHandlers,
    websocketUrl,
  } = useHistoryAisApi();

  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [showDateRangeLoading, setShowDateRangeLoading] = useState(false);
  const [showDateRangeError, setShowDateRangeError] = useState(false);
  const [showDateRangeSuccess, setShowDateRangeSuccess] = useState(false);

  const isMobile = useMobile();

  const toggleShowDateRangeModal = () => {
    setShowDateRangeModal((prev) => !prev);
    if (!showDateRangeModal) {
      setShowDateRangeLoading(false);
      setShowDateRangeError(false);
      setShowDateRangeSuccess(false);
    }
  };

  const onCalendarSubmit = (
    start: Date | null,
    end: Date | null,
    formik: ReturnType<typeof useFormik<HistoryFormValues>>
  ) => {
    dispatch(
      setHistoryFormValues({
        fromDate: start?.toISOString().split('T')[0],
        toDate: end?.toISOString().split('T')[0],
      })
    );
    formik.setFieldValue('fromDate', start?.toISOString().split('T')[0]);
    formik.setFieldValue('toDate', end?.toISOString().split('T')[0]);
  };

  const formik = useFormik({
    initialValues: {
      fromDate: formValues.fromDate,
      toDate: formValues.toDate,
      sampleRate: formValues.sampleRate,
      identifiers: formValues.identifiers,
    },
    validationSchema: historyValidationSchema,
    onSubmit,
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
  });

  const identifierCount = splitIdentifiers(formik.values.identifiers).length;
  const showLargeRequestWarning =
    formik.isValid &&
    (identifierCount > WARN_IDENTIFIER_COUNT ||
      isDateRangeOverOneYear(formik.values.fromDate, formik.values.toDate));

  return (
    <>
      <FormikProvider value={formik}>
        <Form
          className={classNames({ mobile: isMobile })}
          onSubmit={formik.handleSubmit}
        >
          <Stack
            sx={{
              width: '90%',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
            }}
            spacing={2}
            direction="column"
          >
            <Box>
              <Typography align="left" sx={{ marginLeft: '0.5rem' }}>
                Filters
              </Typography>
            </Box>
            <Box>
              <IconButton
                onClick={() => toggleShowDateRangeModal()}
                sx={{
                  borderBottom: '1px solid',
                  borderBottomColor: 'background.paper',
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'space-between',
                  minHeight: '40px',
                  padding: '6px 24px 6px 16px',
                  fontSize: '16px',
                  fontWeight: '400',
                  color: 'text.primary',
                  borderRadius: '0',
                  width: '100%',
                  marginBottom: '1.0rem',
                  marginTop: '0.5rem',
                }}
              >
                {formik.values.fromDate} - {formik.values.toDate}
                <ArrowRight />
              </IconButton>
              <MuiSelectFieldControlled
                name="sampleRate"
                placeholder="Select sample rate"
                options={[
                  {
                    name: 'Every 10 minutes',
                    value: HistoryPanelSampleRate.TEN_MINUTES,
                  },
                  { name: 'Hourly', value: HistoryPanelSampleRate.ONE_HOUR },
                  { name: 'Daily', value: HistoryPanelSampleRate.ONE_DAY },
                ]}
                onChange={(e) => {
                  dispatch(
                    setHistoryFormValues({ sampleRate: e.target.value })
                  );
                }}
                errors={formik.errors}
                touched={formik.touched}
                label="Sample Rate"
                ariaLabel="sample-rate-input"
                value={formik.values.sampleRate}
              />
            </Box>
            <Divider />
            <Stack direction="column" spacing={2}>
              <Stack direction="column" spacing={1} sx={{ maxHeight: '35rem' }}>
                <Typography align="left">Vessel Identifiers</Typography>
                <TextareaAutosize
                  aria-label="Vessel Identifiers"
                  minRows={5}
                  placeholder="Enter white-space separated IMOs & MMSIs here. IMO is the preferred search criteria."
                  id="identifiers"
                  name="identifiers"
                  style={{ maxWidth: '100%', minWidth: '100%' }}
                  onChange={(e) => {
                    dispatch(
                      setHistoryFormValues({ identifiers: e.target.value })
                    );
                  }}
                  defaultValue={formik.values.identifiers}
                />
                {formik.errors.identifiers && (
                  <FormHelperText error>
                    {formik.errors.identifiers}
                  </FormHelperText>
                )}
                {showLargeRequestWarning && (
                  <ErrorPanel level="warning" message={LARGE_REQUEST_WARNING} />
                )}

                {loading && (
                  <LoadingPanel
                    loadingMessage={
                      typeof loading === 'string' ? loading : undefined
                    }
                  />
                )}
                {isLoadingSlowly && (
                  <ErrorPanel
                    level="warning"
                    message="Websocket connection is taking a long time to load points. Please try again later."
                  />
                )}
                {error && !loading && <ErrorPanel message={ERROR_FETCH} />}
                {noResults && !loading && !error && (
                  <ErrorPanel message={ERROR_NO_RESULTS} />
                )}
              </Stack>

              <Button
                data-testid="history-panel-get-button"
                type="submit"
                disabled={!formik.isValid || !!loading}
                variant="contained"
              >
                Get History
              </Button>
              <Collapse in={isActive}>
                <Button
                  data-testid="history-panel-cancel-button"
                  type="button"
                  variant="outlined"
                  sx={{ width: '100%' }}
                  onClick={onCancel}
                >
                  Stop Loading Points
                </Button>
              </Collapse>
            </Stack>
          </Stack>
        </Form>
      </FormikProvider>
      <DateRangeModal
        title="Select a date range"
        loading={showDateRangeLoading}
        success={showDateRangeSuccess}
        error={showDateRangeError}
        visible={showDateRangeModal}
        onClose={() => toggleShowDateRangeModal()}
        onSelect={(modalResult) => {
          onCalendarSubmit(
            modalResult.dateFrom.toDate(),
            modalResult.dateTo.toDate(),
            formik
          );
          toggleShowDateRangeModal();
        }}
        initialStart={formValues.fromDate}
        initialEnd={formValues.toDate}
        variant={EDateRangeModalVariants.HISTORY_TWO_YEARS}
      />
      {isActive && websocketUrl && (
        <WebSocket
          url={new URL(websocketUrl)}
          signUrl={false}
          onConnectionOpened={websocketHandlers.onConnectionOpened}
          onConnectionClosed={websocketHandlers.onConnectionClosed}
          onConnectionError={websocketHandlers.onConnectionError}
          onMessageReceived={websocketHandlers.onMessageReceived}
        />
      )}
    </>
  );
}

export default HistoryFormAisApi;
